<script setup lang="ts">
import { onMounted, Ref, ref, toValue } from 'vue';
import {
  Contexts,
  getContainer,
  NamedBindings,
  TranslationService,
  UserServiceInterface,
} from '@dandelion/core';
import { useRouter } from 'vue-router';
import { NamedServices } from '../../enum/NamedServices';
import { PreferencesService } from '../../services/PreferencesService';
import { centrePreferencesWithPossibleValues } from '../../entities/PreferenceValueConfigs';
import PreferencesMultiSelectorPanel from './../UserPreferences/PreferencesMultiSelectorPanel.vue';
import { Preferences } from '../../enum/Preferences';
import { Users } from '../../enum/Users';
import { Preference, PreferenceValueConfig } from '../../entities/Preference';

const router = useRouter();
const sharedContainer = getContainer(Contexts.SHARED);
const ts = sharedContainer.get(
  NamedBindings.TranslationService
) as TranslationService;
const centrePreferencesService = sharedContainer.get(
  NamedServices.CentrePreferencesService
) as PreferencesService;
const centrePreferencesDialogRef = ref<HTMLDialogElement | null>(null);

const preferencesValues = centrePreferencesService.getPreferences().reduce(
  (result, p) => {
    // We detach reactivity here: we want an internal copy, that eventually will be synchronized
    // with the service on the "confirm" click
    result[p.preferenceName] = ref(toValue(p.value));
    return result;
  }, {} as Record<Preferences, Ref<string>>
);

const preferencesConfig = centrePreferencesWithPossibleValues.reduce(
  (result, p) => {
    result[p.name] = p.possibleValues.reduce(
      (r, v) => {
        r[v.value] = v;
        return r;
      }, {} as Record<string, PreferenceValueConfig>
    );
    return result;
  }, {} as Record<Preferences, Record<string, PreferenceValueConfig>>
);

const fetchFiles = async () => {
  try {
    const response = await fetch('/downloadNames.json');
    const data = await response.json();
    files.value = Array.isArray(data[centreCode]) ? data[centreCode] : [];
  } catch (error) {
    console.error('Error fetching file names:', error);
  }
};
const showDropdown = ref(false);
const userService = getContainer().get(
  NamedBindings.UserService
) as UserServiceInterface;
const centreCode = userService.getCurrentUser()?.value?.centre;
const isAdmin = userService.getCurrentUser().value?.role === Users.ADMIN;

const files = ref<string[]>([]);
const downloadFile = (fileName: string) => {
  const fileUrl = `${window.location.origin}/files/${centreCode}/${fileName}`;
  const link = document.createElement('a');
  link.href = fileUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

onMounted(fetchFiles);

const currentPanel: Ref<Preferences> = ref(Preferences.CENTREPREFERENCES);

function toggleValue(preferenceName: Preferences) {
  const currentValue = preferencesValues[preferenceName];
  const possibleValues = Object.keys(preferencesConfig[preferenceName]);
  if (possibleValues.length != 2) {
    console.error(`
      There was an error when toggling preferences: the preference ${preferenceName}
      has ${possibleValues.length} option, but 'toggleValue',
      which supports only two options, has been called on it!
    `);
  }
  return currentValue.value === possibleValues[0] ? possibleValues[1] : possibleValues[0];
}

function showPanel() {
  if (centrePreferencesDialogRef?.value) {
    centrePreferencesDialogRef.value.showModal();
  }
}

function close() {
  currentPanel.value = Preferences.CENTREPREFERENCES
  centrePreferencesDialogRef.value?.close();
}

async function confirm() {
  for (const [ preference, value ] of Object.entries(preferencesValues)) {
    await centrePreferencesService.setPreference(preference as Preferences, value.value);
  }

  router.go(0);
}

function onPreferenceClick(preference: Preference) {
  if (preference.possibleValues?.length > 2) {
    currentPanel.value = preference.name;
  } else if (preference.name == Preferences.USERCONTROLPANEL) {
    if ([ Users.SUPPORT_ADMIN, Users.ADMIN ].includes(userService.getCurrentUser().value?.role)) {
        window.location.href = `${import.meta.env.VITE_APP_AUTH_BASE_URL}UserManager`;
    }
  } else {
    preferencesValues[preference.name].value = toggleValue(preference.name);
  }
}

function onValueSelected(preferenceName: Preferences, newValue: string) {
  preferencesValues[preferenceName].value = newValue;
  currentPanel.value = Preferences.CENTREPREFERENCES;
}

</script>

<template>
  <dialog ref="centrePreferencesDialogRef" class="preferences-dialog">
    <template v-if="currentPanel == Preferences.CENTREPREFERENCES">
      <header>
        <h2 v-html="ts.t('Centre preferences')"></h2>
        <ion-icon name="close-outline" @click="close()"></ion-icon>
      </header>
      <main>
        <div
          v-for="preference in centrePreferencesWithPossibleValues"
          class="preferences-dialog-card"
          v-if="isAdmin"
        >
          <div
            :class="{
              'preferences-dialog-icon-wrapper': true,
              [preference.name.toLowerCase()]: true,
              'icon-active': [ 'true', 'dark' ].includes(preferencesValues[preference.name].value)
            }"
            @click="onPreferenceClick(preference)"
          >
            <ion-icon
              :name="preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.icon"
            ></ion-icon>
          </div>
          <p
            class="preferences-dialog-description"
            v-html="
              ts.t(preference.label) +
              ts.t(preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.description)
            "
          ></p>
          <span
            v-if="preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.icon"
            :class="preferencesConfig[preference.name]?.[preferencesValues[preference.name].value]?.icon"
          ></span>
        </div>
        <div class="preferences-dialog-card">
          <div
            @mouseenter="showDropdown = true"
            @mouseleave="showDropdown = false"
            :class="{ 'preferences-dialog-icon-wrapper': true }"
          >
            <ion-icon name="document-outline"></ion-icon>
            <div v-if="showDropdown" class="dropdown">
              <ul class="dropdown-ul">
                <li v-for="file in files" :key="file">
                  <button class="doc-button" @click="downloadFile(file)">
                    {{ file }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <p
            class="preferences-dialog-description"
            v-html="ts.t('Download Documentation')"
          ></p>
        </div>
      </main>
      <footer>
        <button
          type="submit"
          @click="confirm()"
          v-html="ts.t('Confirm')"
        ></button>
      </footer>
    </template>

    <PreferencesMultiSelectorPanel
      v-if="currentPanel == Preferences.GCPSTRATEGY"
      title="GCP strategy"
      :options="preferencesConfig[Preferences.GCPSTRATEGY]"
      :selectedOption="preferencesValues[Preferences.GCPSTRATEGY]"
      @close="close()"
      @back-to-preferences="currentPanel = Preferences.CENTREPREFERENCES"
      @value-selected="(newValue: string) => onValueSelected(Preferences.GCPSTRATEGY, newValue)"
    >
    </PreferencesMultiSelectorPanel>

    <PreferencesMultiSelectorPanel
      v-if="currentPanel == Preferences.TYPEIMPORT"
      title="Import Type"
      :options="preferencesConfig[Preferences.TYPEIMPORT]"
      :selectedOption="preferencesValues[Preferences.TYPEIMPORT]"
      @close="close()"
      @back-to-preferences="currentPanel = Preferences.CENTREPREFERENCES"
      @value-selected="(newValue: string) => onValueSelected(Preferences.TYPEIMPORT, newValue)"
    >
    </PreferencesMultiSelectorPanel>

  </dialog>
  
  <button
    id="centre-preferences-dialog-btn"
    @click="showPanel()"
  >
    <ion-icon name="business-outline"></ion-icon>
    <ion-icon name="settings-outline"></ion-icon>
  </button>
</template>

<style scoped>
.dropdown {
  position: absolute;
  top: 50%;
  left: 0;
  background: white;
  border: 1px solid #ccc;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  padding: 5px;
  min-width: 150px;
}

.dropdown-ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li {
  margin: 5px 0;
}

.doc-button {
  padding: 5px 10px;
  font-size: 14px;
  background-color: #f8f9fa;
  color: #333;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
  border-radius: 3px;
}

</style>
